import {
  DataTypeCustomerContext,
  DataTypeLocation,
  DataTypeOrder,
  DataTypeOrderLine,
  DataTypeProduct,
  OrderRequest,
} from 'api-order-client'

import { BaseCartProduct, Cart } from '../model/cart.model'
import {
  DetailedOrder,
  OrderDetailsData,
  Orderline,
} from '../model/order.model'
import { formatDateAsNaiveDate } from '../utils/misc'
import { Price } from './price.transforms'
import { transformOrderlineProductToProductData } from './product.transforms'

export const orderLineToPartialBaseCartProduct = (
  orderLine: Omit<Orderline, 'batches'>,
): Omit<BaseCartProduct, 'customerContext'> => ({
  data: transformOrderlineProductToProductData(orderLine.product),
  orderAmount: orderLine.orderAmount,
  price: Price.fromMonetaryAmount(orderLine.subtotalValue),
  pricePoints: [],
  quantity: orderLine.quantityCalculated,
})

export const orderDetailsDataToBaseCartProduct = (
  orderDetailsData: OrderDetailsData,
): Omit<BaseCartProduct, 'customerContext'> => ({
  ...orderLineToPartialBaseCartProduct(orderDetailsData),
  quantity: orderDetailsData.quantity,
})

export type OrderRequestWithOptionalMeta = Omit<OrderRequest, 'meta'> &
  Partial<Pick<OrderRequest, 'meta'>>

export const transformDetailedOrderToOrder = (
  detailedOrder: DetailedOrder,
  customerContextId: string,
  soldToId: string,
): OrderRequestWithOptionalMeta => {
  return {
    data: {
      attributes: {
        totalValue: detailedOrder.summary.totalValue,
      },
      id: detailedOrder.id,
      relationships: {
        billTo: {
          data: {
            id: detailedOrder.summary.billToAddress?.id || '',
            type: DataTypeLocation.Location,
          },
        },
        customerContext: {
          data: {
            id: customerContextId,
            type: DataTypeCustomerContext.CustomerContext,
          },
        },
        orderLines: {
          data:
            detailedOrder.orders?.map(({ id }) => ({
              id,
              type: DataTypeOrderLine.OrderLine,
            })) || [],
        },
        shipTo: {
          data: {
            id: detailedOrder.summary.shipToAddress?.id || '',
            type: DataTypeLocation.Location,
          },
        },
        soldTo: {
          data: {
            id: soldToId,
            type: DataTypeLocation.Location,
          },
        },
      },
      type: DataTypeOrder.Order,
    },
    included:
      detailedOrder.orders?.map(
        ({ id, orderAmount, product, subtotalValue }) => ({
          attributes: {
            orderAmount,
            subtotalValue,
          },

          id,
          relationships: {
            order: {
              data: {
                id: detailedOrder.id,
                type: DataTypeOrder.Order,
              },
            },
            product: {
              data: {
                id: product.id,
                type: DataTypeProduct.Product,
              },
            },
          },
          type: DataTypeOrderLine.OrderLine,
        }),
      ) || [],
  }
}

export const transformCartToOrder = (
  cart: Cart,
  soldToId?: string,
): OrderRequestWithOptionalMeta => ({
  data: {
    attributes: {
      commentFromUser: cart.customerComments,
      purchaseOrderNumber: cart.customerReference,
      ...(cart.deliveryDate && {
        requestedDeliveryDate: formatDateAsNaiveDate(
          new Date(cart.deliveryDate),
        ),
      }),
      totalValue: cart.totalValue && cart.totalValue.toMonetaryAmount(),
    },
    id: '#newOrderId#',
    relationships: {
      billTo: {
        data: {
          id: cart.billToLocation?.id || '',
          type: DataTypeLocation.Location,
        },
      },
      customerContext: {
        data: {
          id: cart.customerContext.customerContextId || '',
          type: DataTypeCustomerContext.CustomerContext,
        },
      },
      orderLines: {
        data: [
          ...cart.products.map((_p, index) => ({
            id: (++index).toString(), //SAP expects these to start from 1
            type: DataTypeOrderLine.OrderLine,
          })),
        ],
      },
      shipTo: {
        data: {
          id: cart.shipToLocation?.id || '',
          type: DataTypeLocation.Location,
        },
      },
      soldTo: {
        data: {
          id: soldToId || '',
          type: DataTypeLocation.Location,
        },
      },
    },
    type: DataTypeOrder.Order,
  },
  included: cart.products.map((p, index) => ({
    attributes: {
      orderAmount: p.orderAmount,
      subtotalValue: p.price && p.price.toMonetaryAmount(),
    },

    id: (++index).toString(), //SAP expects these to start from 1
    relationships: {
      order: {
        data: {
          id: '#newOrderId#',
          type: DataTypeOrder.Order,
        },
      },
      product: {
        data: {
          id: p.data.id,
          type: DataTypeProduct.Product,
        },
      },
    },
    type: DataTypeOrderLine.OrderLine,
  })),
  ...(cart.platformId && { meta: { platformId: cart.platformId } }),
})
